// src/components/TurnstileWidget.tsx
import { useEffect, useMemo, useRef, useState } from 'react'

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, params: any) => string | undefined
      remove: (widgetId: string) => void
      reset: (widgetId: string) => void
      getResponse: (widgetId: string) => string
    }
  }
}

type Props = {
  siteKey: string
  onToken: (token: string) => void

  // ✅ 추가 옵션
  action?: string // 예: "login" | "signup" | "forgot"
  cData?: string // 필요시
  size?: 'flexible' | 'normal' | 'compact'
  hidden?: boolean // ✅ 성공 후 숨김용
  resetKey?: string | number // ✅ 이 값이 바뀌면 reset
}

export function TurnstileWidget({
  siteKey,
  onToken,
  action = 'auth',
  cData,
  size = 'flexible',
  hidden = false,
  resetKey
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)
  const [ready, setReady] = useState(false)

  const renderParams = useMemo(
    () => ({
      sitekey: siteKey,
      theme: 'auto',
      action,
      cData,
      size,
      callback: (token: string) => onToken(token),
      'expired-callback': () => onToken(''), // 만료되면 토큰 비움
      'error-callback': () => onToken('')
    }),
    [siteKey, action, cData, size, onToken]
  )

  // 최초 렌더
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.turnstile && containerRef.current && !widgetIdRef.current) {
        const id = window.turnstile.render(containerRef.current, renderParams)
        widgetIdRef.current = id ?? null
        setReady(true)
        clearInterval(interval)
      }
    }, 50)

    return () => {
      clearInterval(interval)
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [renderParams])

  // reset 트리거
  useEffect(() => {
    if (!resetKey) return
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current)
    }
  }, [resetKey])

  return (
    <div className={`w-full ${hidden ? 'hidden' : ''}`}>
      <div ref={containerRef} className="w-full flex justify-center" />
      {!ready && <div className="mt-2 text-xs opacity-70">Loading…</div>}
    </div>
  )
}
